import type { FeatureGridBlockProps } from '../types';
import { theme, sectionColors, cssColor, cardGridClass, DEFAULT_CARD_GRID } from '../theme';

const ICON_MAP: Record<string, string> = {
  home: '🏠', 'home-search': '🏠', house: '🏠', search: '🔍',
  shield: '🛡️', 'shield-check': '🛡️', lock: '🔒', star: '⭐',
  chart: '📈', 'chart-line': '📈', graph: '📈', data: '📊',
  heart: '❤️', trophy: '🏆', award: '🏅', medal: '🏅',
  users: '👥', team: '👥', user: '👤', handshake: '🤝',
  headset: '🎧', phone: '📞', mail: '✉️', email: '✉️',
  key: '🔑', map: '🗺️', pin: '📍', location: '📍',
  money: '💲', dollar: '💲', tag: '🏷️', clock: '⏰',
  check: '✓', 'check-circle': '✓', sparkles: '✨', building: '🏢',
};

/**
 * Map a common icon hint (name or emoji) to a glyph. Dependency-free (this package
 * is framework-agnostic — no icon library), so an unknown name falls back to a dot.
 * Authors can also put an emoji directly in the `icon` field — any non-ASCII string
 * is used verbatim.
 */
export function iconGlyph(icon?: string): string {
  if (!icon) return '◆';
  const raw = icon.trim();
  if (!raw) return '◆';
  // A non-ASCII string is almost certainly an emoji the author typed → use as-is.
  for (const ch of raw) {
    if (ch.codePointAt(0)! > 127) return raw;
  }
  return ICON_MAP[raw.toLowerCase()] ?? '◆';
}

// Line icons for `iconStyle: 'line'` — the outline glyphs the built-in About page always drew
// (Heroicons outline paths, 24×24). A name not listed here falls back to the emoji glyph.
const LINE_ICONS: Record<string, string> = {
  map: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  'shield-check': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
};

/**
 * Pure feature/values grid — icon + heading + body tiles (why-us, values, services).
 * Static content; renders nothing when empty.
 */
export function FeatureGridBlock(props: FeatureGridBlockProps) {
  const { heading, subheading, features = [], iconStyle } = props;
  const line = iconStyle === 'line';
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  if (!Array.isArray(features) || features.length === 0) return null;

  // "Cards per row" (when set) wins over the count-based default.
  const grid = cardGridClass(props, features.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : DEFAULT_CARD_GRID);

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-6xl">
        {heading ? (
          <h2
            className="text-center text-2xl font-semibold"
            style={{ fontFamily: theme.headingFont }}
          >
            {heading}
          </h2>
        ) : null}
        {subheading ? (
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm opacity-70">{subheading}</p>
        ) : null}
        <div className={`mt-8 grid gap-6 ${grid}`}>
          {features.map((f, i) => (
            <div key={i} className="blk-card rounded-lg border border-black/10 p-6">
              {line && f.icon && LINE_ICONS[f.icon.trim().toLowerCase()] ? (
                <div
                  className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl"
                  style={{
                    backgroundColor: cssColor(props.iconBgColor) ?? `color-mix(in srgb, ${theme.primary} 12%, transparent)`,
                    color: cssColor(props.iconColor) ?? theme.primary,
                  }}
                  aria-hidden="true"
                >
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={LINE_ICONS[f.icon.trim().toLowerCase()]} />
                  </svg>
                </div>
              ) : (
                <div
                  className="mb-3 flex h-11 w-11 items-center justify-center rounded-full text-lg"
                  style={{ backgroundColor: cssColor(props.iconBgColor) ?? theme.accent, color: cssColor(props.iconColor) ?? theme.secondary }}
                  aria-hidden="true"
                >
                  {iconGlyph(f.icon)}
                </div>
              )}
              <h3 className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                {f.heading}
              </h3>
              <p className="mt-2 text-sm leading-relaxed opacity-70">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
